import companyContingenciaService from "../services/companyContingenciaService";
import { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import contingenciaService from "../services/contingenciaService";
import api from "../services/api";
import Swal from "sweetalert2";

const TIPOS_CONTINGENCIA = [
  { value: 1, label: "No disponibilidad de sistema del contribuyente" },
  { value: 2, label: "No disponibilidad de sistema del Ministerio de Hacienda" },
  { value: 3, label: "Falla de energía eléctrica o internet (contribuyente)" },
  { value: 4, label: "Falla de energía eléctrica o internet (comprobada por proveedor)" },
  { value: 5, label: "Otras causas" },
];

export default function CreateContingenciaModal({ onClose, onSuccess }) {
  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fInicio, setFInicio] = useState("");
  const [fFin, setFFin] = useState("");
  const [hInicio, setHInicio] = useState("");
  const [hFin, setHFin] = useState("");
  const [tipoContingencia, setTipoContingencia] = useState(5);
  const [motivoContingencia, setMotivoContingencia] = useState("");

  const [loadingEstado, setLoadingEstado] = useState(true);

  // 🆕 Pre-llenar tipo, motivo y fechas/horas desde el estado ya guardado
  // al activar/desactivar contingencia, en vez de pedirlo de nuevo.
  useEffect(() => {
    const fetchEstadoContingencia = async () => {
      try {
        const estado = await companyContingenciaService.getEstado();

        if (estado.contingencia_tipo) {
          setTipoContingencia(estado.contingencia_tipo);
        }
        if (estado.contingencia_motivo) {
          setMotivoContingencia(estado.contingencia_motivo);
        }
        if (estado.contingencia_fecha_inicio) {
          const inicio = new Date(estado.contingencia_fecha_inicio);
          setFInicio(inicio.toISOString().split("T")[0]);
          setHInicio(inicio.toTimeString().slice(0, 5));
        }
        if (estado.contingencia_fecha_fin) {
          const fin = new Date(estado.contingencia_fecha_fin);
          setFFin(fin.toISOString().split("T")[0]);
          setHFin(fin.toTimeString().slice(0, 5));
        } else {
          // Respaldo: si aún no hay fecha_fin guardada, usar el momento actual
          const ahora = new Date();
          setFFin(ahora.toISOString().split("T")[0]);
          setHFin(ahora.toTimeString().slice(0, 5));
        }
      } catch (error) {
        console.error("No se pudo precargar el estado de contingencia:", error);
        Swal.fire(
          "Error",
          "No se pudo obtener el período de contingencia automáticamente. Contacta a soporte antes de continuar.",
          "error"
        );
      } finally {
        setLoadingEstado(false);
      }
    };

    fetchEstadoContingencia();
  }, []);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoadingInvoices(true);
        const response = await api.get("/invoices/elegibles-evento-contingencia");
        setInvoices(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error cargando facturas:", error);
        Swal.fire("Error", "No se pudieron cargar las facturas", "error");
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, []);

  const toggleSelect = (invoice) => {
    setSelectedIds((prev) =>
      prev.includes(invoice.id)
        ? prev.filter((id) => id !== invoice.id)
        : [...prev, invoice.id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((inv) => inv.id));
    }
  };

  const validateForm = () => {
    if (selectedIds.length === 0) {
      Swal.fire("Error", "Debe seleccionar al menos una factura", "warning");
      return false;
    }
    if (!fInicio || !fFin || !hInicio || !hFin) {
      Swal.fire("Error", "Debe completar fecha y hora de inicio/fin", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const facturasSeleccionadas = invoices.filter((inv) => selectedIds.includes(inv.id));

    const confirm = await Swal.fire({
      title: "¿Enviar Evento de Contingencia?",
      html: `
        <p><strong>Facturas incluidas:</strong> ${facturasSeleccionadas.length}</p>
        <p><strong>Período:</strong> ${fInicio} ${hInicio} - ${fFin} ${hFin}</p>
        <p><strong>Motivo:</strong> ${TIPOS_CONTINGENCIA.find(t => t.value === parseInt(tipoContingencia))?.label}</p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#7c3aed",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setIsSubmitting(true);

    try {
      const payload = {
        detalle: facturasSeleccionadas.map((inv) => ({
          tipoDoc: inv.tipo_documento,
          codigoGeneracion: inv.codigo_generacion,
        })),
        fInicio,
        fFin,
        hInicio: hInicio + ":00",
        hFin: hFin + ":00",
        tipoContingencia: parseInt(tipoContingencia),
        motivoContingencia: motivoContingencia.trim() || null,
      };

      const response = await contingenciaService.create(payload);

      Swal.fire({
        icon: "success",
        title: "¡Evento Enviado!",
        html: `<p>El evento de contingencia se envió correctamente a Hacienda.</p>`,
        confirmButtonText: "Aceptar",
      });

      onSuccess();
    } catch (error) {
      console.error("Error creando evento de contingencia:", error);
      Swal.fire("Error", error.response?.data?.error || error.message || "No se pudo enviar el evento", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content card" style={{ maxHeight: "90vh" }}>
            <div className="card-header">
              <h3 className="card-title" style={{ margin: 0 }}>⚠️ Nuevo Evento de Contingencia</h3>
              <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={isSubmitting}>
                <X size={18} />
              </button>
            </div>

            <div className="card-body" style={{ overflowY: "auto" }}>
              <form onSubmit={handleSubmit}>
                <div className="alert alert-info" style={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "start" }}>
                  <AlertCircle size={20} style={{ marginTop: "2px", flexShrink: 0 }} />
                  <div style={{ fontSize: "14px" }}>
                    <strong>¿Qué es un Evento de Contingencia?</strong>
                    <p style={{ margin: "8px 0 0 0" }}>
                      Reporta a Hacienda los DTEs que se emitieron sin conexión durante una falla,
                      dentro del plazo permitido.
                    </p>
                  </div>
                </div>

                <div className="alert alert-secondary" style={{ marginBottom: "16px", fontSize: "13px" }}>
                  {loadingEstado ? (
                    <span>Calculando período de contingencia...</span>
                  ) : (
                    <>
                      <strong>Período reportado:</strong> {fInicio} {hInicio} — {fFin} {hFin}
                      <div style={{ fontSize: "11px", color: "#718096", marginTop: "4px" }}>
                        Calculado automáticamente según cuándo activaste y desactivaste el modo contingencia.
                      </div>
                    </>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label className="form-label"><strong>Tipo de Contingencia</strong></label>
                  <select className="form-control" value={tipoContingencia} onChange={(e) => setTipoContingencia(e.target.value)}>
                    {TIPOS_CONTINGENCIA.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label className="form-label"><strong>Motivo (detalle adicional)</strong></label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Ej: Se perdió la conexión a internet por 2 horas..."
                    value={motivoContingencia}
                    onChange={(e) => setMotivoContingencia(e.target.value)}
                    maxLength={500}
                  />
                </div>

                <div className="form-group">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <label className="form-label" style={{ margin: 0 }}>
                      <strong>Facturas a incluir ({selectedIds.length} seleccionadas)</strong>
                    </label>
                    {!loadingInvoices && invoices.length > 0 && (
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", fontWeight: 600, color: "#7c3aed" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.length === invoices.length}
                          onChange={toggleSelectAll}
                          style={{ width: "16px", height: "16px" }}
                        />
                        Seleccionar todas
                      </label>
                    )}
                  </div>
                  <div style={{ maxHeight: "250px", overflowY: "auto", border: "2px solid #e2e8f0", borderRadius: "8px", background: "#f7fafc" }}>
                    {loadingInvoices ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <div className="spinner" style={{ margin: "0 auto" }}></div>
                      </div>
                    ) : invoices.length === 0 ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <p style={{ color: "#a0aec0", fontSize: "14px" }}>No hay facturas de contingencia pendientes de reportar</p>
                      </div>
                    ) : (
                      invoices.map((inv) => (
                        <div
                          key={inv.id}
                          onClick={() => toggleSelect(inv)}
                          style={{
                            padding: "10px 12px",
                            borderBottom: "1px solid #e2e8f0",
                            cursor: "pointer",
                            background: selectedIds.includes(inv.id) ? "#ede9fe" : "white",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <input type="checkbox" checked={selectedIds.includes(inv.id)} readOnly style={{ width: "16px", height: "16px" }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "13px", fontWeight: 600 }}>
                              {inv.sale?.client?.nombre || "N/A"} — {inv.tipo_documento === "01" ? "CF" : "CCF"}
                            </div>
                            <div style={{ fontSize: "10px", color: "#6b7280", fontFamily: "monospace" }}>
                              {inv.codigo_generacion}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="card-body" style={{ borderTop: "2px solid #e2e8f0", display: "flex", gap: "12px", paddingTop: "var(--spacing-md)" }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting} style={{ flex: 1 }}>
                Cancelar
              </button>
              <button
                className="btn"
                onClick={handleSubmit}
                disabled={isSubmitting || loadingEstado || selectedIds.length === 0}
                style={{ flex: 1, background: "#7c3aed", color: "#fff" }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Enviando...
                  </>
                ) : (
                  "✓ Enviar Evento"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}